import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  RefreshCw, Copy, Download, Eye, Sparkles, Plus,
  Building2, Mail, Phone, Globe, Calendar, ChevronDown,
  X, Check, AlertCircle, Search, FileText, CheckCircle2, Circle, Filter, Bell, Loader2
} from 'lucide-react';
import HelpBubble from './HelpBubble';
import UpsellCampaignBuilder from './UpsellCampaignBuilder';
import { BACKEND_URL } from '../../utils/api';

const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = ['new', 'in_progress', 'submitted', 'active', 'inactive', 'contacted', 'qualified', 'completed'];

function getProgressPercent(entry, defaultStages) {
  if (!defaultStages || defaultStages.length === 0) return 0;
  const completed = (entry.progress || []).filter(s => s.completed).length;
  return Math.round((completed / defaultStages.length) * 100);
}

function ProgressBar({ percent }) {
  const color = percent === 100 ? 'bg-emerald-500' : percent > 50 ? 'bg-blue-500' : percent > 0 ? 'bg-blue-400' : 'bg-slate-200';
  return (
    <div className="flex items-center gap-2 min-w-[100px]" data-testid="progress-bar">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${percent}%` }} />
      </div>
      <span className={`text-xs font-medium ${percent === 100 ? 'text-emerald-600' : 'text-slate-500'}`}>{percent}%</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    new: 'bg-blue-600',
    in_progress: 'bg-blue-500',
    submitted: 'bg-blue-700',
    active: 'bg-blue-600',
    inactive: 'bg-slate-500',
    contacted: 'bg-slate-600',
    qualified: 'bg-blue-700',
    completed: 'bg-blue-800',
  };
  return <Badge className={colors[status] || 'bg-slate-500'}>{status?.replace('_', ' ') || 'unknown'}</Badge>;
}

function EntryDetailModal({ entry, productTypes, onClose, onStatusUpdate, onAIExport, onProgressUpdate, defaultStages, getAuthHeaders }) {
  const [newStatus, setNewStatus] = useState(entry.status);
  const [saving, setSaving] = useState(false);
  const [stages, setStages] = useState(() => {
    if (entry.progress && entry.progress.length > 0) return entry.progress;
    return (defaultStages || []).map(s => ({ ...s, completed: false, completed_at: null }));
  });
  const config = productTypes[entry.product_type] || {};

  const handleSave = async () => {
    if (newStatus === entry.status) return;
    setSaving(true);
    await onStatusUpdate(entry.product_type, entry.id, newStatus);
    setSaving(false);
  };

  const toggleStage = async (idx) => {
    const updated = stages.map((s, i) => {
      if (i === idx) return { ...s, completed: !s.completed, completed_at: !s.completed ? new Date().toISOString() : null };
      return s;
    });
    setStages(updated);
    await onProgressUpdate(entry.product_type, entry.id, updated);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="entry-detail-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg">{entry.business_name || 'Unknown Business'}</h3>
            <span className="text-sm text-slate-500">{config.label} Onboarding</span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="font-medium text-slate-600">Contact:</span> {entry.contact_name || '—'}</div>
            <div><span className="font-medium text-slate-600">Email:</span> <a href={`mailto:${entry.contact_email}`} className="text-blue-600 hover:underline">{entry.contact_email || '—'}</a></div>
            <div><span className="font-medium text-slate-600">Phone:</span> {entry.contact_phone || '—'}</div>
            <div><span className="font-medium text-slate-600">Website:</span> {entry.website ? <a href={entry.website.startsWith('http') ? entry.website : `https://${entry.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{entry.website}</a> : '—'}</div>
            <div><span className="font-medium text-slate-600">Package:</span> {entry.package || '—'}</div>
            <div><span className="font-medium text-slate-600">Created:</span> {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '—'}</div>
          </div>

          {entry.raw_data && Object.keys(entry.raw_data).length > 0 && (
            <div>
              <h4 className="font-medium text-slate-700 mb-2">Additional Data</h4>
              <pre className="bg-slate-50 p-3 rounded border text-xs overflow-x-auto max-h-48">{JSON.stringify(entry.raw_data, null, 2)}</pre>
            </div>
          )}

          {/* Progress Checklist */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-slate-700 mb-3">Onboarding Progress</h4>
            <div className="space-y-2" data-testid="progress-checklist">
              {stages.map((stage, idx) => (
                <button
                  key={stage.key}
                  onClick={() => toggleStage(idx)}
                  className={`flex items-center gap-3 w-full text-left px-3 py-2 rounded-md border transition-all text-sm ${
                    stage.completed
                      ? 'bg-blue-50 border-blue-200 text-blue-800'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`stage-${stage.key}`}
                >
                  {stage.completed
                    ? <CheckCircle2 className="h-4.5 w-4.5 text-blue-600 flex-shrink-0" />
                    : <Circle className="h-4.5 w-4.5 text-slate-300 flex-shrink-0" />
                  }
                  <span className={stage.completed ? 'font-medium' : ''}>{stage.label}</span>
                  {stage.auto && <span className="text-[10px] text-blue-400 ml-1">(auto)</span>}
                  {stage.completed && stage.completed_at && (
                    <span className="ml-auto text-xs text-blue-500">{new Date(stage.completed_at).toLocaleDateString()}</span>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-3">
              <ProgressBar percent={Math.round((stages.filter(s => s.completed).length / stages.length) * 100)} />
            </div>
          </div>

          <div className="flex items-center gap-3 border-t pt-4">
            <label className="text-sm font-medium text-slate-600">Status:</label>
            <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="border rounded px-3 py-1.5 text-sm" data-testid="status-select">
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
            <Button size="sm" onClick={handleSave} disabled={saving || newStatus === entry.status} data-testid="save-status-btn">
              {saving ? 'Saving...' : 'Update Status'}
            </Button>
          </div>

          <div className="flex gap-2 border-t pt-4">
            <Button size="sm" onClick={() => onAIExport(entry.product_type, entry.id)} className="bg-blue-600 hover:bg-blue-700" data-testid="ai-export-btn">
              <Sparkles className="h-4 w-4 mr-1" /> AI Generation
            </Button>
            <Button size="sm" variant="outline" onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(entry.raw_data || entry, null, 2));
              alert('Copied to clipboard!');
            }} data-testid="copy-data-btn">
              <Copy className="h-4 w-4 mr-1" /> Copy Data
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateEntryModal({ productTypes, onClose, onCreate, getAuthHeaders }) {
  const [formData, setFormData] = useState({
    product_type: '', business_name: '', contact_name: '', contact_email: '', phone: '', website: '', package: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product_type || !formData.business_name) return;
    setSaving(true);
    await onCreate(formData);
    setSaving(false);
    onClose();
  };

  const selectedConfig = productTypes[formData.product_type];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="create-entry-modal">
      <div className="bg-white rounded-lg max-w-lg w-full">
        <div className="border-b p-4 flex justify-between items-center">
          <h3 className="font-bold text-lg">New Onboarding Entry</h3>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-3">
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Product Type *</label>
            <select value={formData.product_type} onChange={(e) => setFormData({ ...formData, product_type: e.target.value, package: '' })} className="w-full border rounded px-3 py-2 text-sm" required data-testid="product-type-select">
              <option value="">Select type...</option>
              {Object.entries(productTypes).map(([key, val]) => <option key={key} value={key}>{val.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Business Name *</label>
            <Input value={formData.business_name} onChange={(e) => setFormData({ ...formData, business_name: e.target.value })} required data-testid="business-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Contact Name</label>
              <Input value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} data-testid="contact-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Email</label>
              <Input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} data-testid="contact-email-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Phone</label>
              <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="phone-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Website</label>
              <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} data-testid="website-input" />
            </div>
          </div>
          {selectedConfig?.packages && (
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Package</label>
              <select value={formData.package} onChange={(e) => setFormData({ ...formData, package: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" data-testid="package-select">
                <option value="">Select package...</option>
                {selectedConfig.packages.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Notes</label>
            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full border rounded px-3 py-2 text-sm h-20 resize-none" data-testid="notes-input" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="create-entry-submit">
              {saving ? 'Creating...' : 'Create Entry'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminOnboarding({ getAuthHeaders }) {
  const [entries, setEntries] = useState([]);
  const [productTypes, setProductTypes] = useState({});
  const [typeCounts, setTypeCounts] = useState({});
  const [defaultStages, setDefaultStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('all');
  const [progressFilter, setProgressFilter] = useState('all'); // 'all' | 'incomplete' | 'complete'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [upsellEntry, setUpsellEntry] = useState(null);

  const fetchData = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/onboarding/all`, { headers });
      setEntries(r.data.entries || []);
      setProductTypes(r.data.product_types || {});
      setTypeCounts(r.data.type_counts || {});
      if (r.data.default_stages) setDefaultStages(r.data.default_stages);
    } catch (err) {
      console.error('Error fetching onboarding data:', err);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusUpdate = async (productType, entryId, newStatus) => {
    const headers = getAuthHeaders();
    try {
      await axios.put(`${API}/admin/onboarding/${productType}/${entryId}/status`, { status: newStatus }, { headers });
      fetchData();
    } catch (err) {
      alert('Failed to update status: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleAIExport = async (productType, entryId) => {
    const headers = getAuthHeaders();
    try {
      const r = await axios.get(`${API}/admin/onboarding/export/${productType}/${entryId}`, { headers });
      await navigator.clipboard.writeText(r.data.text);
      // Update local progress if backend returned updated stages (auto-trigger)
      if (r.data.updated_progress) {
        setEntries(prev => prev.map(e =>
          e.product_type === productType && e.id === entryId ? { ...e, progress: r.data.updated_progress } : e
        ));
      }
      alert('AI-ready data copied to clipboard! Paste into a new AI chat to build the website.');
    } catch (err) {
      alert('Export failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCreateEntry = async (formData) => {
    const headers = getAuthHeaders();
    try {
      await axios.post(`${API}/admin/onboarding/create`, formData, { headers });
      fetchData();
    } catch (err) {
      alert('Failed to create entry: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleProgressUpdate = async (productType, entryId, stages) => {
    const headers = getAuthHeaders();
    try {
      const r = await axios.put(`${API}/admin/onboarding/${productType}/${entryId}/progress`, { stages }, { headers });
      // Update local state for instant feedback
      setEntries(prev => prev.map(e =>
        e.product_type === productType && e.id === entryId ? { ...e, progress: stages } : e
      ));
      // Check if transition happened (delivered stage completed)
      if (r.data.transition) {
        const t = r.data.transition;
        let msg = 'Client transitioned to Active status!';
        if (t.archived) msg += '\nOnboarding data archived for safekeeping.';
        if (t.drip_enrolled) msg += '\nUpsell drip campaign started.';
        alert(msg);
        fetchData(); // Refresh to reflect new status
      }
    } catch (err) {
      console.error('Failed to update progress:', err);
    }
  };

  const filteredEntries = entries.filter(e => {
    const matchesType = activeType === 'all' || e.product_type === activeType;
    const matchesSearch = !searchQuery || [e.business_name, e.contact_name, e.contact_email].some(f => f?.toLowerCase().includes(searchQuery.toLowerCase()));
    const pct = getProgressPercent(e, defaultStages);
    const isActive = e.status === 'active';
    const matchesProgress =
      progressFilter === 'all' ||
      (progressFilter === 'active' && isActive) ||
      (progressFilter === 'incomplete' && !isActive && pct < 100) ||
      (progressFilter === 'complete' && pct === 100);
    return matchesType && matchesSearch && matchesProgress;
  });

  const totalEntries = entries.length;
  const [sendingAllReminders, setSendingAllReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);

  const sendAllReminders = async () => {
    if (!window.confirm('Send reminder emails to ALL clients with incomplete onboarding?')) return;
    setSendingAllReminders(true);
    setReminderResult(null);
    try {
      const res = await fetch(`${API}/admin/onboarding/send-all-reminders`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setReminderResult({ type: 'success', msg: `Reminders sent to ${data.sent} clients (${data.skipped} skipped)` });
      } else {
        setReminderResult({ type: 'error', msg: 'Failed to send reminders' });
      }
    } catch (err) {
      setReminderResult({ type: 'error', msg: err.message });
    }
    setSendingAllReminders(false);
    setTimeout(() => setReminderResult(null), 5000);
  };

  const sortedTypes = Object.entries(productTypes).sort((a, b) => (typeCounts[b[0]] || 0) - (typeCounts[a[0]] || 0));

  return (
    <div className="space-y-6" data-testid="admin-onboarding">
      {/* Header */}
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-slate-900">Onboarding</h2>
          <Badge className="bg-blue-600">{totalEntries} total</Badge>
          <HelpBubble
            title="Unified Onboarding Dashboard"
            steps={[
              'View all client onboarding entries across every product type in one place',
              'Use tabs to filter by product category (Car & RV Dealers, Enterprise, etc.)',
              'Click "AI Generation" to export data for building websites with AI',
              'Click "Review" to see full details and update status',
              'Use "+ New Entry" to manually add onboarding records',
            ]}
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={sendAllReminders} variant="outline" size="sm" disabled={sendingAllReminders} className="text-amber-600 border-amber-300 hover:bg-amber-50" data-testid="send-all-reminders-btn">
            {sendingAllReminders ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Bell className="h-4 w-4 mr-1" />}
            {sendingAllReminders ? 'Sending...' : 'Remind All Incomplete'}
          </Button>
          <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => setShowCreateModal(true)} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="new-entry-btn">
            <Plus className="h-4 w-4 mr-1" /> New Entry
          </Button>
        </div>
        {reminderResult && (
          <div className={`w-full text-sm px-3 py-2 rounded ${reminderResult.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {reminderResult.msg}
          </div>
        )}
      </div>

      {/* Product Type Tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="product-type-tabs">
        <Button
          variant={activeType === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveType('all')}
          className={activeType === 'all' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          data-testid="tab-all"
        >
          All ({totalEntries})
        </Button>
        {sortedTypes.map(([key, config]) => {
          const count = typeCounts[key] || 0;
          if (count === 0 && activeType !== key) return null;
          return (
            <Button
              key={key}
              variant={activeType === key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveType(key)}
              className={activeType === key ? 'bg-blue-600 hover:bg-blue-700' : ''}
              data-testid={`tab-${key}`}
            >
              {config.label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Search + Progress Filter */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-testid="progress-filter">
          {[
            { key: 'all', label: 'All' },
            { key: 'incomplete', label: 'Needs Follow-up' },
            { key: 'complete', label: 'Fully Onboarded' },
            { key: 'active', label: 'Active Clients' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setProgressFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                progressFilter === f.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`progress-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12 text-slate-500" data-testid="loading-state">
          <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin text-blue-600" />
          Loading onboarding data...
        </div>
      ) : filteredEntries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500" data-testid="empty-state">
            <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No onboarding entries found</p>
            <p className="text-sm mt-1">
              {searchQuery ? 'Try a different search term' : activeType !== 'all' ? 'No entries for this product type' : 'Click "New Entry" to create one'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="onboarding-table">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    {activeType === 'all' && <th className="px-4 py-3 text-left font-medium text-slate-600">Type</th>}
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Business</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Contact</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Package</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Progress</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry, idx) => {
                    const config = productTypes[entry.product_type] || {};
                    return (
                      <tr key={`${entry.product_type}-${entry.id}-${idx}`} className="border-b last:border-0 hover:bg-slate-50 transition-colors" data-testid={`entry-row-${idx}`}>
                        {activeType === 'all' && (
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="text-xs whitespace-nowrap">{config.label || entry.product_type}</Badge>
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{entry.business_name || '—'}</div>
                          {entry.contact_email && <div className="text-xs text-slate-500">{entry.contact_email}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div>{entry.contact_name || '—'}</div>
                          {entry.contact_phone && <div className="text-xs text-slate-500">{entry.contact_phone}</div>}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{entry.package || '—'}</td>
                        <td className="px-4 py-3"><StatusBadge status={entry.status} /></td>
                        <td className="px-4 py-3">
                          <ProgressBar percent={getProgressPercent(entry, defaultStages)} />
                        </td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                          {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1.5 justify-end flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-2.5 text-xs"
                              onClick={() => handleAIExport(entry.product_type, entry.id)}
                              title="Export for AI Generation"
                              data-testid={`ai-gen-btn-${idx}`}
                            >
                              <Sparkles className="h-3.5 w-3.5 mr-1" /> AI Generation
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-2.5 text-xs"
                              onClick={() => setSelectedEntry(entry)}
                              title="Review entry details"
                              data-testid={`review-btn-${idx}`}
                            >
                              <Eye className="h-3.5 w-3.5 mr-1" /> Review
                            </Button>
                            {(entry.status === 'active' || getProgressPercent(entry, defaultStages) === 100) && (
                              <Button
                                size="sm"
                                className="h-8 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => setUpsellEntry(entry)}
                                title="Build upsell drip campaign"
                                data-testid={`upsell-btn-${idx}`}
                              >
                                <Mail className="h-3.5 w-3.5 mr-1" /> Upsell Campaign
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail Modal */}
      {selectedEntry && (
        <EntryDetailModal
          entry={selectedEntry}
          productTypes={productTypes}
          onClose={() => { setSelectedEntry(null); fetchData(); }}
          onStatusUpdate={handleStatusUpdate}
          onAIExport={handleAIExport}
          onProgressUpdate={handleProgressUpdate}
          defaultStages={defaultStages}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateEntryModal
          productTypes={productTypes}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateEntry}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {/* Upsell Campaign Builder */}
      {upsellEntry && (
        <UpsellCampaignBuilder
          entry={upsellEntry}
          productTypes={productTypes}
          onClose={() => setUpsellEntry(null)}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </div>
  );
}
